import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import { img } from "@/lib/api";
import Marquee from "@/components/Marquee";
import { Reveal, Eyebrow, MaskedLine } from "@/components/Reveal";

const CHAPTERS = [
  { n: "01", title: "Discipline", text: "Greatness doesn't require noise. Discipline creates progress — the reps nobody watches, the hours nobody applauds. Jae Noir was built on the belief that the quiet work is the only work that compounds." },
  { n: "02", title: "Vision", text: "Vision provides direction. Before the brand had a name, it had a standard: heavyweight fabrics, honest construction, meaning stitched into every piece. You move different because you see different." },
  { n: "03", title: "Loyalty", text: "Loyalty establishes character. Loyalty to the people who believed early, to the craft when shortcuts were cheaper, and to the version of yourself you promised you'd become." },
  { n: "04", title: "Faith", text: "Faith keeps you moving when results aren't visible yet. Every drop is released on faith — made slowly, in limited numbers, for the people who understand. Built in silence. Move in faith. Leave a legacy." },
];

export default function Story() {
  return (
    <div className="pt-16" data-testid="story-page">
      <div className="relative h-[60svh] overflow-hidden">
        <img src={img("story.jpg")} alt="The Jae Noir atelier" className="w-full h-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-t from-noir-bg via-noir-bg/30 to-noir-bg/60" />
        <div className="absolute bottom-0 left-0 right-0 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-14">
          <MaskedLine delay={0.1}><span className="font-mono2 text-[10px] sm:text-xs tracking-[0.4em] uppercase text-gold">Our Story — Est. 2026</span></MaskedLine>
          <h1 className="font-display font-extrabold uppercase tracking-tight text-5xl sm:text-7xl mt-4 leading-[0.9]">
            <MaskedLine delay={0.2}><span className="text-cream">Built in</span></MaskedLine>
            <MaskedLine delay={0.3}><span className="text-outline">Silence</span></MaskedLine>
          </h1>
        </div>
      </div>

      <Marquee />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-24 sm:py-32">
        <Reveal>
          <p className="text-cream/70 text-lg sm:text-2xl font-light leading-relaxed max-w-3xl" data-testid="story-intro">
            Jae Noir was created around one belief — that greatness doesn't require noise. The brand represents people who move differently, work quietly, trust their vision, and let their results speak for them.
          </p>
        </Reveal>

        <div className="mt-20 sm:mt-28 space-y-0">
          {CHAPTERS.map((c, i) => (
            <Reveal key={c.n} delay={0.05}>
              <div className="grid grid-cols-1 md:grid-cols-12 gap-6 py-12 sm:py-16 border-t border-white/10" data-testid={`story-chapter-${c.n}`}>
                <div className="md:col-span-3">
                  <span className="font-mono2 text-gold text-sm">Chapter {c.n}</span>
                </div>
                <div className="md:col-span-4">
                  <h2 className="font-display text-2xl sm:text-4xl font-bold uppercase">{c.title}</h2>
                </div>
                <div className="md:col-span-5">
                  <p className="text-cream/50 text-sm sm:text-base leading-relaxed">{c.text}</p>
                </div>
              </div>
            </Reveal>
          ))}
        </div>

        <Reveal>
          <div className="mt-20 border border-gold/30 p-10 sm:p-16 text-center" data-testid="story-creed">
            <img src={img("logo-full.png")} alt="Jae Noir crest — panther and alligator shield" className="w-40 sm:w-52 mx-auto mb-8" />
            <Eyebrow className="justify-center">The Creed</Eyebrow>
            <p className="font-display text-2xl sm:text-4xl font-bold uppercase mt-6 leading-snug">
              Discipline <span className="text-gold">•</span> Vision <span className="text-gold">•</span> Loyalty
            </p>
            <p className="mt-6 text-cream/50 text-sm max-w-lg mx-auto leading-relaxed">
              The clothes are the medium. The message is the standard. When you wear Jae Noir, you carry the reminder: build quietly, move with purpose, leave something behind.
            </p>
            <Link to="/shop?drop=new" className="inline-flex items-center gap-3 mt-10 bg-gold text-noir-bg px-8 py-4 font-mono2 text-[10px] tracking-[0.25em] uppercase hover:bg-cream transition-colors duration-300" data-testid="story-shop-cta">
              Shop the Drop <ArrowRight size={13} />
            </Link>
          </div>
        </Reveal>
      </div>
    </div>
  );
}
